import React, { useState } from 'react';

import AnimateHeight from 'react-animate-height';
import Button from '~/components/ui/Button';
import Input from '~/components/ui/Input';
import Modal from '~/components/ui/Modal';
import PropTypes from 'prop-types';
import { clone as cloneObject } from 'lodash';
import cn from 'classnames';
import { withFilterContext } from '~/context/FilterContext';

/**
 * FilterCategory is, for example, for filtering "Colors" to "Red" and "Blue" and
 * lies within a FilterSection
 */
const FilterCategory = ({
  categoryKey,
  filter,
  activeFilters = {},
  applyFilterChange,
  className = '',
}) => {
  const [expanded, setExpanded] = useState(true);
  const [showAllFilters, setShowAllFilters] = useState(false);

  const defaultVisibleFilters = 8;
  const { label, values, type } = filter;

  const removeEmptyOptions = options => options.filter(option => option);

  return (
    <div className={className}>
      <Button
        className="w-full text-left text-small lg:text-small-lg mb-2 capitalize"
        attrs={{ onClick: () => setExpanded(!expanded) }}
        button={{
          text: label,
          style: 'text',
          rounded: false,
          icon: expanded ? 'minus' : 'plus',
        }}
      />

      <AnimateHeight
        height={expanded ? 'auto' : 0}
        className="border-b border-grey"
      >
        <div className="pt-2 pb-4 flex flex-col">
          {(showAllFilters
            ? removeEmptyOptions(values)
            : removeEmptyOptions(values.slice(0, defaultVisibleFilters))
          ).map((option, index) => {
            let checked = false;

            // Check if the type is a boolean before assuming it is an array
            if (
              activeFilters[categoryKey] &&
              (activeFilters[categoryKey] === true ||
                activeFilters[categoryKey].includes(option))
            ) {
              checked = true;
            }

            return (
              <Input
                key={`filter-${categoryKey}-${option}`}
                onValue={newCheckedValue =>
                  applyFilterChange(categoryKey, option, newCheckedValue, type)
                }
                className={cn({
                  'mb-4': index < values.length - 1,
                })}
                attrs={{
                  type: 'checkbox',
                  id: `filter-${categoryKey}-${option}`,
                  checked,
                  labelClassName: 'text-tiny',
                }}
                input={{ label: option }}
              />
            );
          })}

          {!showAllFilters && values.length > defaultVisibleFilters && (
            <Button
              attrs={{
                onClick: () => setShowAllFilters(true),
              }}
              className="block text-grey text-tiny text-left leading-filters"
              button={{
                text: 'See more',
                style: 'text',
              }}
            />
          )}
        </div>
      </AnimateHeight>
    </div>
  );
};

/**
 * FilterSection is the entire filtering area. This is a component because it is
 * replicated within the sidebar and the modal.
 */
const FilterSection = ({
  resetFilters,
  filters,
  activeFilters,
  applyFilterChange,
}) => (
  <>
    <div className="flex justify-between mt-1/2 mb-2 pb-2 border-b border-grey">
      <span>Filters</span>

      <Button
        className="text-grey text-tiny lg:text-tiny-lg self-end"
        attrs={{
          onClick: resetFilters,
        }}
        button={{ text: 'Clear', style: 'text' }}
      />
    </div>

    {Object.keys(filters).map((categoryKey, index) => (
      <FilterCategory
        categoryKey={categoryKey}
        key={`filter-${categoryKey}-${index}`}
        filter={filters[categoryKey]}
        activeFilters={activeFilters}
        applyFilterChange={applyFilterChange}
        className="mt-4"
      />
    ))}
  </>
);

/**
 * CollectionFilters is the main component.
 *
 * The "tempActiveFilters" and "tempActiveFilterCount" are used within the
 * modal before the "Apply" button is selected. These values get reset to
 * their default values whenever the modal is closed and set to the values
 * of "activeFilters" and "activeFilterCount" when it is opened to
 * maintain consistency.
 */
const CollectionFilters = ({
  filters = {},
  onFilterUpdate,
  sidebarClassName,
  toolbarClassName,
  activeFilters,
  setActiveFilters,
  activeFilterCount,
}) => {
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [tempActiveFilters, setTempActiveFilters] = useState({});
  const [tempActiveFilterCount, setTempActiveFilterCount] = useState(0);
  const [tempResetFilters, setTempResetFilters] = useState(false);

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const applyFilterChange = (filter, option, on, type) => {
    /**
     * showMobileFilters is used extensively in this function to ensure we're
     * updating either the temp values or the actual values prior to applying
     * the changes via the "Apply" button in the modal
     */
    const activeFilterObject = cloneObject(
      showMobileFilters ? tempActiveFilters : activeFilters,
    );

    /**
     * Build out the new filter object based on the filter (Color), the
     * option (Red, Blue) and account for it being a potential boolean
     * from additioanlOptions
     */
    if (on) {
      if (!activeFilterObject[filter]) {
        activeFilterObject[filter] = [];
      }

      if (!activeFilterObject[filter].includes(option)) {
        if (type === 'Boolean') {
          activeFilterObject[filter] = on;
        } else {
          activeFilterObject[filter].push(option);
        }
      }
    } else {
      if (activeFilterObject[filter]) {
        if (activeFilterObject[filter] === true) {
          delete activeFilterObject[filter];
        } else if (activeFilterObject[filter].includes(option)) {
          if (activeFilterObject[filter].length === 1) {
            delete activeFilterObject[filter];
          } else {
            const optionIndexInFilters =
              activeFilterObject[filter].indexOf(option);

            activeFilterObject[filter].splice(optionIndexInFilters, 1);
          }
        }
      }
    }

    if (showMobileFilters) {
      setTempResetFilters(false);
      setTempActiveFilterCount(tempActiveFilterCount + (on ? 1 : -1));
      setTempActiveFilters(activeFilterObject);
    } else {
      setActiveFilters(activeFilterObject);
      onFilterUpdate(activeFilterObject);
    }
  };

  const beforeApplyResetFilters = () => {
    if (showMobileFilters) {
      setTempActiveFilters({});
      setTempActiveFilterCount(0);
      setTempResetFilters(true);
    } else {
      applyResetFilters();
    }
  };

  const applyResetFilters = () => {
    setActiveFilters({});
    setTempActiveFilters({});
    setTempActiveFilterCount(0);
    onFilterUpdate({});
  };

  const openModal = () => {
    setShowMobileFilters(true);
    setTempActiveFilters(activeFilters);
    setTempActiveFilterCount(activeFilterCount);
    setTempResetFilters(false);
  };

  const closeModal = applyFilters => {
    if (applyFilters) {
      if (tempResetFilters) {
        applyResetFilters();
      } else {
        setActiveFilters(tempActiveFilters);
        onFilterUpdate(tempActiveFilters);
      }
    }

    setTempResetFilters(false);
    setTempActiveFilters({});
    setTempActiveFilterCount(0);
    setShowMobileFilters(false);
  };

  return (
    <>
      <div className={sidebarClassName}>
        <FilterSection
          resetFilters={beforeApplyResetFilters}
          filters={filters}
          activeFilters={activeFilters}
          applyFilterChange={applyFilterChange}
        />
      </div>

      <div className={toolbarClassName}>
        <Button
          className="text-left px-4 py-2 text-button w-full border border-grey rounded"
          attrs={{
            onClick: openModal,
          }}
          button={{
            text: `Filters (${activeFilterCount})`,
            style: 'secondary',
          }}
        />
      </div>

      <Modal
        open={showMobileFilters}
        contentLabel={`Filters (${activeFilterCount})`}
        onRequestClose={() => closeModal(false)}
        disableCloseButton
        containerClassName="h-full"
        contentClassName="flex flex-col"
      >
        <FilterSection
          resetFilters={beforeApplyResetFilters}
          filters={filters}
          activeFilters={tempActiveFilters}
          applyFilterChange={applyFilterChange}
        />

        <div className="flex-grow flex items-end">
          <Button
            attrs={{ onClick: () => closeModal(true) }}
            className="w-full mt-4 mb-6"
            button={{ text: 'Apply' }}
          />
        </div>
      </Modal>
    </>
  );
};

CollectionFilters.propTypes = {
  filters: PropTypes.object,
  onFilterUpdate: PropTypes.func,
  sidebarClassName: PropTypes.string,
  toolbarClassName: PropTypes.string,
  activeFilters: PropTypes.object,
  setActiveFilters: PropTypes.func,
  activeFilterCount: PropTypes.number,
};

export default React.memo(withFilterContext(CollectionFilters));
